import { FanWall } from "@/components/site/FanWall";
import React, { useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { publicApi, formatApiErrorDetail } from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";

const inputStyle = { border: "var(--rule)", borderRadius: "var(--radius)", color: "var(--parchment)", background: "transparent" };
const cls = "w-full px-4 py-3 outline-none focus:border-[var(--gold)]";

export const StayConnected = ({ presetInterest }) => {
  const [sub, setSub] = useState({ email: "", first_name: "", interest: presetInterest || "New music", consent: false, website: "" });
  const [msg, setMsg] = useState({ name: "", email: "", subject: "", message: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submitSub = async (e) => {
    e.preventDefault();
    if (!sub.consent) return toast.error("Please tick the consent box to subscribe");
    setBusy(true);
    try {
      const { data } = await publicApi.post("/public/subscribe", sub);
      setDone(true);
      toast.success(data.message || "You're on the list.");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitMsg = async (e) => {
    e.preventDefault();
    if (!msg.name || !msg.email || !msg.message) return toast.error("Please fill in name, email and message");
    setBusy(true);
    try {
      await publicApi.post("/public/contact", msg);
      toast.success("Message sent. We'll be in touch.");
      setMsg({ name: "", email: "", subject: "", message: "", website: "" });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <section id="connect" className="section-pad" data-testid="connect-section">
      <div className="mx-auto max-w-[720px] px-5 sm:px-8">
        <Reveal className="text-center">
          <p className="font-caps text-gold" style={{ fontSize: ".72rem" }}>Stay Connected</p>
          <h2 className="font-display foil-text mt-3" style={{ fontSize: "var(--t-h2)", lineHeight: 1.05 }}>First listen, first dibs</h2>
          <FanWall />
        </Reveal>

        <Reveal delay={0.05} className="frame p-6 sm:p-8 mt-10" style={{ backgroundColor: "var(--ink-raised)" }}>
          <Tabs defaultValue="mailing">
            <TabsList className="grid grid-cols-2 mb-6" style={{ background: "transparent" }}>
              <TabsTrigger value="mailing" data-testid="tab-mailing" className="font-caps" style={{ fontSize: ".6rem" }}>Mailing list</TabsTrigger>
              <TabsTrigger value="contact" data-testid="tab-contact" className="font-caps" style={{ fontSize: ".6rem" }}>Contact</TabsTrigger>
            </TabsList>

            <TabsContent value="mailing">
              {done ? (
                <p className="text-center font-display text-gold py-8" style={{ fontSize: "1.4rem" }}>You&rsquo;re on the list. First listen, first dibs.</p>
              ) : (
                <form className="grid gap-4" onSubmit={submitSub} data-testid="mailing-form">
                  <input type="text" tabIndex={-1} autoComplete="off" value={sub.website} onChange={(e) => setSub({ ...sub, website: e.target.value })} className="hidden" aria-hidden="true" />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input className={cls} style={inputStyle} placeholder="First name" value={sub.first_name} onChange={(e) => setSub({ ...sub, first_name: e.target.value })} data-testid="sub-name" />
                    <input className={cls} style={inputStyle} type="email" placeholder="Email" value={sub.email} onChange={(e) => setSub({ ...sub, email: e.target.value })} data-testid="sub-email" required />
                  </div>
                  <select className={cls} style={inputStyle} value={sub.interest} onChange={(e) => setSub({ ...sub, interest: e.target.value })} data-testid="sub-interest">
                    <option style={{ background: "var(--ink)" }}>New music</option>
                    <option style={{ background: "var(--ink)" }}>Merch</option>
                    <option style={{ background: "var(--ink)" }}>Press &amp; media</option>
                  </select>
                  <label className="flex items-start gap-3" style={{ color: "var(--ash)", fontSize: ".82rem" }}>
                    <input type="checkbox" checked={sub.consent} onChange={(e) => setSub({ ...sub, consent: e.target.checked })} data-testid="sub-consent" className="mt-1" />
                    I agree to receive emails from Heart N Soul Records. Unsubscribe anytime.
                  </label>
                  <button className="btn-gold justify-center" disabled={busy} data-testid="sub-submit">{busy ? "Joining…" : "Join the list"}</button>
                </form>
              )}
            </TabsContent>

            <TabsContent value="contact">
              <form className="grid gap-4" onSubmit={submitMsg} data-testid="contact-form">
                <input type="text" tabIndex={-1} autoComplete="off" value={msg.website} onChange={(e) => setMsg({ ...msg, website: e.target.value })} className="hidden" aria-hidden="true" />
                <p style={{ color: "var(--ash)", fontSize: ".82rem" }}>For press, media, radio, and appearance inquiries.</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input className={cls} style={inputStyle} placeholder="Name" value={msg.name} onChange={(e) => setMsg({ ...msg, name: e.target.value })} data-testid="msg-name" />
                  <input className={cls} style={inputStyle} type="email" placeholder="Email" value={msg.email} onChange={(e) => setMsg({ ...msg, email: e.target.value })} data-testid="msg-email" />
                </div>
                <input className={cls} style={inputStyle} placeholder="Subject" value={msg.subject} onChange={(e) => setMsg({ ...msg, subject: e.target.value })} data-testid="msg-subject" />
                <textarea className={cls} style={inputStyle} rows={4} placeholder="Your message" value={msg.message} onChange={(e) => setMsg({ ...msg, message: e.target.value })} data-testid="msg-message" />
                <button className="btn-gold justify-center" disabled={busy} data-testid="msg-submit">{busy ? "Sending…" : "Send message"}</button>
              </form>
            </TabsContent>
          </Tabs>
        </Reveal>
      </div>
    </section>
  );
};
